import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import devLogInstance from "../../lib/loggerConfig";

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

// One shared promise for the whole app. Under StrictMode every effect runs
// twice, and several forms can mount at once, so without this the script tag
// would be injected repeatedly.
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Turnstile script failed")));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null; // let a later mount retry
      reject(new Error("Turnstile script failed"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  // Called when the token expires or the challenge fails, so the form can clear
  // the stale token it is holding.
  onExpire?: () => void;
  className?: string;
  theme?: "light" | "dark" | "auto";
}

/**
 * Renders the Cloudflare Turnstile challenge. Usually invisible - most visitors
 * only see a brief "verifying" box.
 *
 * Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is unset, so a missing key
 * degrades to "no widget" rather than a broken form. The backend fails open in
 * the same situation.
 */
const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  ({ onVerify, onExpire, className, theme = "auto" }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [failedToLoad, setFailedToLoad] = useState(false);

    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    // Keep the latest callbacks in refs so re-renders never force a re-render of
    // the widget itself, which would invalidate the token the user just earned.
    const onVerifyRef = useRef(onVerify);
    const onExpireRef = useRef(onExpire);
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (window.turnstile && widgetIdRef.current)
          window.turnstile.reset(widgetIdRef.current);
      },
    }));

    useEffect(() => {
      if (!siteKey) {
        devLogInstance.log("Turnstile site key not configured, skipping widget");
        return;
      }

      let cancelled = false;

      loadTurnstileScript()
        .then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;
          // StrictMode can run this effect twice; do not render a second widget
          if (widgetIdRef.current) return;

          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme,
            callback: (token: string) => onVerifyRef.current(token),
            "expired-callback": () => onExpireRef.current?.(),
            "error-callback": () => onExpireRef.current?.(),
          });
        })
        .catch((err) => {
          if (cancelled) return;
          devLogInstance.error(err);
          setFailedToLoad(true);
        });

      return () => {
        cancelled = true;
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [siteKey, theme]);

    if (!siteKey) return null;

    // Cloudflare unreachable. Say nothing alarming and let the submit proceed -
    // the server still has the honeypot, timing, scoring and rate limits.
    if (failedToLoad) return null;

    return <div ref={containerRef} className={className} />;
  }
);

TurnstileWidget.displayName = "TurnstileWidget";

export default TurnstileWidget;
